/**
 * Publish the SELECTED questionnaire as a broker surface.
 *
 * One questionnaire at a time, deliberately: `CowbellRiskEngQuestions` renders
 * exactly one, and a snapshot describing all of an account's forms would offer
 * proposal targets that are not on screen and cannot be staged.
 *
 * Values are the PROJECTION — a pending draft where one exists, the working
 * answer otherwise — because that is what the user is looking at. Each drafted
 * field also carries `draft`, so a consumer can tell an unreviewed suggestion
 * from an answer the user actually gave.
 */
import type {
  ViewSurfaceField,
  ViewSurfaceOption,
  ViewSurfaceSection,
  ViewSurfaceSnapshotDraft,
} from '@cowbell/surface-broker';
import {
  ElementType,
  FieldType,
} from '../../../../../form-engine/parsing/parser';
import type {
  FormFieldDef,
  FormSchema,
} from '../../../../../form-engine/types';
import { collectFieldIds } from '../../../../../form-engine/utils/fieldIds';
import {
  displayOf,
  isOpaqueField,
  optionsOf,
  sameAnswer,
  toScalar,
} from '../fieldValue';
import type { RiskDataDraftState } from '../types';
import { visibleAnswer, workingAnswer } from '../reducer';

export const RISK_DATA_VIEW_ID = 'account-risk-data-form';

export const riskDataViewKey = (accountId: string, formId: string): string =>
  `account-risk-data:${accountId}:${formId}`;

const ENUM_TYPES: readonly FieldType[] = [
  FieldType.Radio,
  FieldType.Checkbox,
  FieldType.SingleSelect,
  FieldType.MultiSelect,
  FieldType.CheckboxGrouping,
  FieldType.Dropdown,
];

function kindOf(def: FormFieldDef): ViewSurfaceField['kind'] {
  if (def.type === FieldType.Currency) {
    return 'currency';
  }
  if (def.type === FieldType.Number) {
    return 'number';
  }
  if (def.type === FieldType.Date) {
    return 'date';
  }
  if (ENUM_TYPES.includes(def.type)) {
    return 'enum';
  }
  return 'text';
}

function toOptions(
  def: FormFieldDef
): readonly ViewSurfaceOption[] | undefined {
  const options = optionsOf(def);
  if (!options) {
    return undefined;
  }
  return options.map((option) => ({
    value: option.value,
    label: option.text,
  }));
}

function buildField(
  state: RiskDataDraftState,
  formId: string,
  def: FormFieldDef
): ViewSurfaceField {
  const visible = visibleAnswer(state, formId, def.id);
  const working = workingAnswer(state, def.id);
  const proposal = state.forms[formId]?.proposals[def.id];
  const options = toOptions(def);

  // A structured answer (percentile lists, job lists) has no scalar form. It is
  // published read-only rather than flattened, because a proposal aimed at one
  // would set a string onto a shape the control reads as an object and fail
  // silently in the renderer instead of loudly at validation.
  const opaque = isOpaqueField(def);

  return {
    path: def.id,
    label: def.text ?? def.key ?? def.id,
    kind: kindOf(def),
    value: toScalar(def, visible),
    display: displayOf(def, visible),
    ...(options ? { options } : {}),
    tier: 'input',
    editable: !opaque,
    dirty: !sameAnswer(working, state.savedAnswers[def.id]),
    ...(proposal
      ? {
          draft: {
            status: proposal.status,
            committedDisplay: displayOf(def, proposal.baselineValue),
            proposedDisplay: displayOf(def, proposal.proposedValue),
            ...(proposal.problem ? { problem: proposal.problem } : {}),
          },
        }
      : {}),
  };
}

export interface RiskDataSnapshotArgs {
  readonly accountId: string;
  readonly formId: string;
  readonly form: FormSchema;
  readonly state: RiskDataDraftState;
  readonly schemaFingerprint: string;
  readonly readOnly: boolean;
  readonly busy: boolean;
}

export function buildRiskDataViewSnapshot({
  accountId,
  formId,
  form,
  state,
  schemaFingerprint,
  readOnly,
  busy,
}: RiskDataSnapshotArgs): ViewSurfaceSnapshotDraft {
  const sections: ViewSurfaceSection[] = (form.elements ?? []).map(
    (element, index) => {
      const ids = collectFieldIds([element]);
      const title =
        element.type === ElementType.Section ? element.title : undefined;
      return {
        kind: 'fields',
        id: `s${index}`,
        label: title || `Section ${index + 1}`,
        fields: ids
          .map((fieldId) => form.fields?.[fieldId])
          .filter((def): def is FormFieldDef => Boolean(def))
          .map((def) => buildField(state, formId, def)),
      };
    }
  );

  const draftCount = Object.keys(state.forms[formId]?.proposals ?? {}).length;

  return {
    viewId: RISK_DATA_VIEW_ID,
    entityType: 'ACCOUNT',
    entityId: accountId,
    title: `${form.name ?? 'Risk questionnaire'} — Risk Data`,
    readOnly,
    busy,
    // Identity a proposal has to quote back. `formId` and `schemaFingerprint`
    // say WHICH questionnaire and which shape of it; `sessionId` says which
    // mount, so a proposal built against a previous one cannot match a
    // remounted revision counter by coincidence.
    summary: {
      formId,
      formName: form.name ?? '',
      schemaFingerprint,
      sessionId: state.sessionId,
      draftAnswers: draftCount,
    },
    sections,
  };
}
